import PropTypes from 'prop-types';
import React from 'react';
import { createRoot } from 'react-dom/client';
import Modal from 'react-bootstrap/Modal';
import { ApiUtil } from '@everfi/reactifi';
import { Button } from '@everfi/reactifi/src/components/Button';
import FontSearchBar from 'lib/components/FontSearchBar';
import i18n from 'lib/utility/i18n';

import { keyTab } from '../../bundles/admin/components/MediaChooserField';
import MediaChooser from '../../bundles/admin/components/MediaChooser';
import { encode } from 'js-base64';
import DOMPurify from 'dompurify';
const { sanitize: sanitizeHTML } = DOMPurify;

export default class Summernote extends React.Component {
  static propTypes = {
    options: PropTypes.object,
    path: PropTypes.string,
    onChange: PropTypes.func,
    defaultValue: PropTypes.string,
    useMediaChooser: PropTypes.bool,
    accountId: PropTypes.string,
    shouldSanitize: PropTypes.bool,
  };

  static defaultProps = {
    useMediaChooser: true,
    shouldSanitize: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      activeMedia: null,
      activeTab: keyTab.MEDIA,
      value: props.defaultValue,
      mediaModalOpen: false,
      mediaModalTarget: null,
      showUnsavedModal: false,
    };

    this.defaultOptions = {
      height: 300,
    };

    this.onChange = this.onChange.bind(this);
    this.mediaSelected = this.mediaSelected.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.openModal = this.openModal.bind(this);
  }

  componentDidMount() {
    let options = Object.assign(this.defaultOptions, this.props.options);
    if (this.props.useMediaChooser) {
      options.buttons.media = context => this.imageButton(context);
    }

    this.node = $(this.refs.textarea).summernote(options);
    this.node.on('summernote.change', this.onChange);
    this.node.summernote('code', this.state.value);

    const root = createRoot(this.refs.textarea);
    root.render(<FontSearchBar node={this.node} textarea={this.refs.textarea} />);
  }

  imageButton(context) {
    let button = $.summernote.ui.button({
      contents: '<i class="note-icon-picture" />',
      tooltip: 'Insert media',
      click: () => {
        this.openModal(context);
      },
    });
    return button.render();
  }

  openModal(context) {
    this.setState({ mediaModalOpen: true, mediaModalTarget: context });
  }

  closeModal() {
    this.setState({ mediaModalOpen: false });
  }

  onChange(e) {
    if (this.node) {
      let newCode = this.node.summernote('code');
      // Keep the text area in sync with summernote
      if (this.props.shouldSanitize) {
        newCode = encode(
          sanitizeHTML(newCode, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
          })
        );
      }
      this.setState({ value: newCode });
      if (this.props.onChange) {
        this.props.onChange(newCode);
      }
    }
  }

  setActiveTab = tab => this.setState({ activeTab: Number(tab) });

  handleChangeActiveMedia = activeMedia => this.setState({ activeMedia });

  onHandleFormChange = event => this.setState({ [event.target.id]: event.target.value });

  onCancelModal = () => (this.state.activeMedia ? this.setState({ showUnsavedModal: true }) : this.closeModal());

  closeModal = () => this.setState({ showUnsavedModal: false, mediaModalOpen: false, activeMedia: null });

  onCloseUnsavedModal = () => this.setState({ showUnsavedModal: false });

  componentWillUnmount() {
    if (this.node) {
      this.node.summernote('destroy');
    }
  }

  confirmModal = () => (
    <Modal
      className="modal-size-small media-confirm-modal"
      backdropClassName="media-confirm-modal"
      show={this.state.showUnsavedModal}
    >
      <Modal.Header>
        <Modal.Title>{i18n.t('Unsaved changes')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <span>{i18n.t('Are you sure you want to leave this page?')}</span>
      </Modal.Body>
      <Modal.Footer>
        <Button className="pull-left" style="secondary" onClick={this.onCloseUnsavedModal}>
          {i18n.t('Cancel')}
        </Button>
        <Button className="pull-right" bsStyle="primary" onClick={this.closeModal}>
          {i18n.t('Yes')}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  mediaModal() {
    if (this.props.useMediaChooser) {
      return (
        <>
          <Modal
            animation={false}
            show={this.state.mediaModalOpen}
            className="modal-size-large"
            onHide={this.closeModal}
          >
            <Modal.Header closeButton={true}>
              <Modal.Title>{i18n.t('Media Chooser')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <MediaChooser
                name="aMediaChooserHasNoName"
                onActiveMediaChange={activeMedia => this.handleChangeActiveMedia(activeMedia)}
                onHandleFormChange={this.onHandleFormChange}
                onDblClickMedia={this.mediaSelected}
                setActiveTab={this.setActiveTab}
                accountId={this.props.accountId}
              />
            </Modal.Body>
            <Modal.Footer className="media-footer">
              <Button data-action="cancel" style="secondary" type="cancel" onClick={this.onCancelModal}>
                {i18n.t('Cancel')}
              </Button>
              <Button
                bsStyle="primary"
                data-action="select-media"
                data-object-id={'activeMedia.id'}
                data-object-type="media"
                onClick={this.mediaSelected}
                disabled={!this.state.activeMedia}
              >
                {i18n.t('Select')}
              </Button>
            </Modal.Footer>
          </Modal>
          {this.confirmModal()}
        </>
      );
    }
  }

  mediaSelected() {
    const { activeMedia, activeTab, media_name, media_description } = this.state;
    const media = {
      name: media_name,
      description: media_description,
      url: activeMedia.url,
    };

    if (this.props.accountId) {
      media.account_id = this.props.accountId;
    }
    const data = { type: 'next_media', attributes: media };

    if (activeTab === keyTab.UPLOAD) {
      ApiUtil.requestApiData(
        `/api/data/next_media`,
        false,
        'POST',
        {
          body: JSON.stringify({ data }),
        },
        false
      );
    }

    this.state.mediaModalTarget.invoke('editor.insertImage', activeMedia.url, $image => {
      $image.attr('alt', media_description);
      $image.attr('title', media_name);
    });
    this.setState({ mediaModalOpen: false, mediaModalTarget: null, activeTab: keyTab.MEDIA });
  }

  render() {
    return (
      <div>
        {this.mediaModal()}
        <textarea
          ref="textarea"
          name={this.props.path}
          value={this.state.value || this.props.defaultValue}
          readOnly={true}
          autoComplete="off"
        />
      </div>
    );
  }
}
